import React, { useContext, useMemo, useRef } from "react";
import type { ReactNode } from "react";
import { ApolloProvider } from "@apollo/client/react";
import {
  ApolloClient,
  ApolloLink,
  HttpLink,
  InMemoryCache,
} from "@apollo/client";
import { onError } from "@apollo/client/link/error";
import { setContext } from "@apollo/client/link/context";
import type { ServerError } from "@apollo/client";
import type { RedirectLoginOptions } from "@auth0/auth0-spa-js";
import env from "@beam-australia/react-env";
import { navigate } from "utils/navigate";
import { some } from "lodash";

import AccessTokenStorage from "utils/AccessTokenStorage";

export interface AuthApolloContextValue {
  tokenLoading?: boolean;
}

export const AuthApolloContext = React.createContext<
  AuthApolloContextValue | undefined
>(undefined);
export const useAuthApollo = () => useContext(AuthApolloContext);

interface AuthApolloInitializerProps {
  tokenLoading?: boolean;
  loading?: boolean;
  // invoked unconditionally on a 401, so required despite the loose propTypes
  loginWithRedirect: (options?: RedirectLoginOptions) => Promise<void>;
  children?: ReactNode;
}

const AuthApolloInitializerComponent = ({
  tokenLoading,
  loading,
  loginWithRedirect,
  children,
}: AuthApolloInitializerProps) => {
  // keep loginWithRedirect accessible from the (stable) error link closure
  const loginWithRedirectRef = useRef(loginWithRedirect);
  loginWithRedirectRef.current = loginWithRedirect;

  const ready = !loading && !tokenLoading;

  // Create ApolloClient once (when auth is ready) and reuse across renders.
  // Previously the client was created inside the render body, which allocated
  // a new client + cache on every navigation (via useLocation re-renders),
  // clearing the cache, re-firing all queries, and cascading re-renders that
  // made the dashboard sidebar disappear on navigation.
  const apolloClient = useMemo(() => {
    if (!ready) return null;

    const authLink = setContext(
      (_, { headers }: { headers?: Record<string, string> }) => {
        const token = AccessTokenStorage.getToken();
        return {
          headers: {
            ...headers,
            authorization: token ? `Bearer ${token}` : "",
          },
        };
      }
    );

    const httpLink = new HttpLink({
      uri: env("BACKEND_URI"),
    });

    return new ApolloClient({
      link: ApolloLink.from([
        onError(({ graphQLErrors, networkError }) => {
          if (graphQLErrors) {
            graphQLErrors.forEach(({ message, locations, path }) => {
              // locations stringifies to "[object Object]" as in the original
              // JS log; preserved rather than reformatted.
              // eslint-disable-next-line @typescript-eslint/no-base-to-string
              const locationStr = String(locations);
              console.log(
                `[GraphQL error]: Message: ${message}, Location: ${locationStr}, Path: ${String(
                  path
                )}`
              );
            });
            const hasLockedError = some(graphQLErrors, (error) =>
              error.message.includes("Individual is locked.")
            );
            if (hasLockedError && window.location.pathname !== "/dashboard") {
              navigate("/dashboard");
            }
          }
          if (networkError) {
            console.log(`[Network error]: ${String(networkError)}`);
            // .response lives on ServerError, not the NetworkError union base;
            // the runtime guard below preserves the original null-safety.
            const serverError = networkError as ServerError;
            if (serverError.response && serverError.response.status === 401) {
              const targetUrl = `${window.location.pathname}${window.location.search}`;
              sessionStorage.setItem("auth0_postLoginRedirect", targetUrl);
              void loginWithRedirectRef.current({
                appState: { targetUrl },
              });
            }
          }
        }),
        authLink,
        httpLink,
      ]),
      cache: new InMemoryCache({ addTypename: false }),
      defaultOptions: {
        watchQuery: {
          fetchPolicy: "no-cache",
          errorPolicy: "ignore",
        },
        query: {
          fetchPolicy: "no-cache",
          errorPolicy: "all",
        },
      },
    });
  }, [ready]);

  const contextValue = useMemo(() => ({ tokenLoading }), [tokenLoading]);

  if (!ready || !apolloClient) {
    return (
      <AuthApolloContext.Provider value={contextValue}>
        {children}
      </AuthApolloContext.Provider>
    );
  }

  return (
    <AuthApolloContext.Provider value={contextValue}>
      <ApolloProvider client={apolloClient}>{children}</ApolloProvider>
    </AuthApolloContext.Provider>
  );
};

export default React.memo(AuthApolloInitializerComponent);
